/**
 * ESPIÃO DE MUDANÇAS — atribuição de causa quando várias mudanças sobem juntas (2026-07-27).
 *
 * <p>CONTEXTO. A rodada de 1.0.300 sobe 4 mudanças de uma vez (S1/F0/BLK/RLD). O risco óbvio é
 * "quebrou algo, mas qual?". Em vez de serializar as builds (cada install na TV custa caro), cada
 * mudança fica REGISTRADA aqui: a caixa-preta passa a gravar QUAIS mudanças estavam ativas e
 * QUANTAS vezes cada uma agiu. Um crash deixa de ser "morreu" e passa a ser "morreu com S1 on,
 * BLK on, RLD off, tendo o BLK agido 3×".
 *
 * <p>KILL-SWITCH SEM REBUILD. Cada mudança pode ser desligada em runtime gravando
 * `efflux_change_override_v1 = {"<ID>":false}` no localStorage (via CDP, `Local dev mode`) e
 * relançando o app. Isso permite bissecção na bancada SEM empacotar/assinar/instalar um WGT novo
 * — que é o gargalo real do ciclo de teste nesta TV (sdb bloqueado, install manual pelo controle).
 *
 * <p>POR QUE localStorage E NÃO uma flag de env: env é build-time; a bissecção precisa ser
 * runtime. E o localStorage é o MESMO bridge que o `crashCapture` já usa pra ler contadores sem
 * importar código de plataforma — mantém o crashCapture platform-agnostic.
 *
 * <p>CUSTO. Escrita SÍNCRONA de localStorage é lenta na Tizen 6.5 e já é suspeita na degradação
 * de sessão longa. Por isso: o estado é publicado UMA VEZ no boot, e `noteChangeAction` acumula
 * em memória com flush THROTTLED (nunca 1 escrita por ação). Nada aqui roda por frame.
 *
 * <p>SÓ localStorage + console — NADA na tela (a bancada fica exposta ao cliente).
 */

/** IDs das mudanças da rodada. Curtos e estáveis — vão pro payload do crash. */
export type ChangeId =
  | 'S1_PREPARE_SYNC_65' // gate isTizen9Plus no prepare (async só no 9+)
  | 'F0_BLACKBOX' // session_restart + bootId + counters zerados no boot
  | 'BLK_RECYCLE_BOOT' // tela preta pura no boot pós-reload (sem UI de sistema)
  | 'RLD_RAM_RELOAD'; // refresh periódico anti-leak (tempo 90min + backstop RAM), no wrap do loop

/**
 * Estado PADRÃO de cada mudança. Todas ON — são os fixes/mitigações oficiais. Cada uma é
 * killable em runtime pelo override (`efflux_change_override_v1`) sem rebuild, caso precise
 * desligar em campo.
 *
 * <p>`RLD_RAM_RELOAD` LIGADO (validado na bancada 2026-07-28: 6 reciclos por tempo numa noite,
 * RAM sempre recuperando, err=0, zero crash — `location.reload()` renova o processo no Tizen 9.0).
 * Desde 2026-09-19 o reload age em TODA plataforma de dispositivo (android/tizen/webos/embedded,
 * nunca browser) e a cada 24h — vide gate em `ramReload.maybeReloadAtLoopWrap`.
 */
const DEFAULT_STATE: Record<ChangeId, boolean> = {
  S1_PREPARE_SYNC_65: true,
  F0_BLACKBOX: true,
  BLK_RECYCLE_BOOT: true,
  RLD_RAM_RELOAD: true,
};

const OVERRIDE_KEY = 'efflux_change_override_v1';
const STATE_KEY = 'efflux_change_state_v1';
const FLUSH_THROTTLE_MS = 10_000;

function readLS(key: string): string | null {
  try {
    return (
      (window as unknown as { localStorage?: Storage }).localStorage?.getItem(
        key,
      ) ?? null
    );
  } catch {
    return null; // localStorage indisponível — o espião degrada pra "só defaults"
  }
}

function writeLS(key: string, value: string): void {
  try {
    (window as unknown as { localStorage?: Storage }).localStorage?.setItem(
      key,
      value,
    );
  } catch {
    /* cheio/indisponível — telemetria é best-effort, nunca quebra playback */
  }
}

/**
 * Overrides de runtime. Lidos UMA VEZ no load do módulo: reler a cada consulta custaria uma
 * leitura síncrona de localStorage por slot, e mudar override no meio da sessão deixaria a
 * atribuição ambígua (metade da sessão com a mudança, metade sem). Override vale a partir do
 * PRÓXIMO boot — que é exatamente o que a bissecção quer.
 */
const overrides: Partial<Record<ChangeId, boolean>> = (() => {
  const raw = readLS(OVERRIDE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Partial<Record<ChangeId, boolean>> = {};
    (Object.keys(DEFAULT_STATE) as ChangeId[]).forEach((id) => {
      if (typeof parsed[id] === 'boolean') out[id] = parsed[id] as boolean;
    });
    return out;
  } catch {
    return {}; // JSON inválido → ignora o override inteiro (não adivinha intenção)
  }
})();

const state: Record<ChangeId, boolean> = { ...DEFAULT_STATE, ...overrides };

/** Quantas vezes cada mudança efetivamente AGIU nesta sessão (não "estava ligada": agiu). */
const actions: Partial<Record<ChangeId, number>> = {};
/** Último detalhe relevante por mudança (ex.: `ram=248MB` no reload) — 40 chars. */
const lastDetail: Partial<Record<ChangeId, string>> = {};

/**
 * BOOT ID — identidade da sessão. A curva opens×RAM da caixa-preta é inútil se misturar
 * sessões; e o `session_restart` precisa amarrar "sessão que morreu" → "sessão que subiu".
 * Sem `Math.random()` (proibido em alguns runtimes de teste): timestamp + contador persistido
 * já dá unicidade suficiente e é ordenável.
 */
const BOOT_SEQ_KEY = 'efflux_boot_seq_v1';
const bootSeq = (() => {
  const n = parseInt(readLS(BOOT_SEQ_KEY) || '0', 10);
  const next = Number.isFinite(n) ? n + 1 : 1;
  writeLS(BOOT_SEQ_KEY, String(next));
  return next;
})();
const bootId = `b${bootSeq}-${Date.now().toString(36)}`;

export function getBootId(): string {
  return bootId;
}

export function getBootSeq(): number {
  return bootSeq;
}

/** A mudança está ativa nesta sessão? Leitura de memória — pode ser chamada por slot. */
export function isChangeOn(id: ChangeId): boolean {
  return state[id];
}

let lastFlushAt = 0;
let dirty = false;

function persistState(force: boolean): void {
  const now = Date.now();
  if (!force && now - lastFlushAt < FLUSH_THROTTLE_MS) {
    dirty = true;
    return;
  }
  lastFlushAt = now;
  dirty = false;
  writeLS(
    STATE_KEY,
    JSON.stringify({
      bootId,
      bootSeq,
      ts: now,
      on: state,
      overridden: Object.keys(overrides),
      actions,
      lastDetail,
    }),
  );
}

/**
 * Registra que uma mudança AGIU. Acumula em memória e faz flush throttled — a diferença entre
 * "ligada" e "agiu" é o que separa "o BLK estava on" de "o BLK pintou preto 3× nesta sessão".
 */
export function noteChangeAction(id: ChangeId, detail?: string): void {
  actions[id] = (actions[id] ?? 0) + 1;
  if (detail) lastDetail[id] = detail.slice(-40);
  persistState(false);
}

/** Snapshot pro HUD/testes/crash payload. */
export function getChangeSpySnapshot(): {
  bootId: string;
  bootSeq: number;
  on: Record<ChangeId, boolean>;
  overridden: string[];
  actions: Partial<Record<ChangeId, number>>;
  lastDetail: Partial<Record<ChangeId, string>>;
} {
  return {
    bootId,
    bootSeq,
    on: { ...state },
    overridden: Object.keys(overrides),
    actions: { ...actions },
    lastDetail: { ...lastDetail },
  };
}

/**
 * Garante que o estado do espião esteja no disco mesmo se a sessão morrer entre flushes. É
 * chamado no `pagehide`; se a morte for nativa (sem evento), o estado do boot ainda está lá
 * porque publicamos na carga do módulo (abaixo).
 */
export function flushChangeSpy(): void {
  if (dirty) persistState(true);
}

// Publica o estado JÁ na carga do módulo: se o app morrer em 30s (como no crash #4), o
// diagnóstico ainda sabe qual configuração estava no ar.
persistState(true);

try {
  window.addEventListener('pagehide', flushChangeSpy);
} catch {
  /* ambiente sem window (teste/SSR) — ignora */
}
